/**
 * Subdomain utility for multi-tenant routing
 *
 * IMPORTANT: For E2E tests, always use URL parameters instead of actual subdomains:
 * - Use: http://localhost:3000/login?subdomain=platform
 * - Use: http://localhost:3000/login?subdomain=test-community
 * - Cookie domain should remain as 'localhost'
 */

// Import logger for better tracking of subdomain-related issues
// Use absolute import path to fix module resolution issues
import { authLogger } from '@/lib/logger';

export type SubdomainType = 'app' | 'platform' | 'community';

export interface ParsedDomain {
  subdomain: string | null;
  type: SubdomainType;
  isCustomDomain: boolean;
}

// IMPORTANT: Default must be 'terrarium.app' in production for custom domain detection to work
// If VITE_APP_DOMAIN is not set, hugthebot.com won't be detected as a custom domain!
const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN || 'terrarium.app';
const PLATFORM_SUBDOMAIN = 'platform';

/**
 * Safely determine if we're in development environment.
 * Avoids direct window references during module initialization,
 * which can cause issues with SSR and during testing.
 */
const isDevelopment = () => {
  try {
    return (
      import.meta.env.DEV ||
      (typeof window !== 'undefined' &&
        (window.location.hostname.includes('localhost') ||
          window.location.hostname.includes('127.0.0.1') ||
          window.location.hostname.endsWith('.lvh.me') ||
          window.location.hostname === 'lvh.me'))
    );
  } catch (error) {
    // Default to false if there's any error accessing window
    authLogger?.warn(
      { error, context: 'isDevelopment' },
      'Error accessing window in isDevelopment'
    );
    return import.meta.env.DEV || false;
  }
};

/**
 * Normalizes subdomain values to either string or null.
 * Handles edge cases like empty strings, undefined values, or strings with only whitespace.
 */
export function normalizeSubdomain(
  subdomain: string | null | undefined
): string | null {
  if (subdomain === undefined || subdomain === null) {
    return null;
  }

  const trimmed = subdomain.trim();

  // Filter out invalid values that might cause encoding issues
  if (trimmed === '' || trimmed.includes('%') || trimmed === 'localhost:3000') {
    return null;
  }

  return trimmed;
}

/**
 * Parses the current domain to determine subdomain information.
 * Enhanced with better error handling for SSR and edge cases.
 */
export function parseDomain(): ParsedDomain {
  try {
    // Special handling for development domains
    if (isDevelopment() && typeof window !== 'undefined') {
      const hostname = window.location.hostname;

      // Handle *.lvh.me format (auto-resolves to 127.0.0.1 via public DNS)
      // No /etc/hosts editing needed - any subdomain works automatically
      if (hostname.endsWith('.lvh.me') || hostname === 'lvh.me') {
        const parts = hostname.split('.'); // e.g., ['dogs', 'lvh', 'me'] or ['lvh', 'me']
        const potentialSubdomain = parts[0];

        if (hostname === 'lvh.me') {
          // Base local domain, represents the 'app' context
          return { subdomain: null, type: 'app', isCustomDomain: false };
        } else if (
          potentialSubdomain === PLATFORM_SUBDOMAIN &&
          hostname === `${PLATFORM_SUBDOMAIN}.lvh.me`
        ) {
          // Handles platform.lvh.me specifically
          return {
            subdomain: PLATFORM_SUBDOMAIN,
            type: 'platform',
            isCustomDomain: false,
          };
        } else {
          // Assumes anything else like dogs.lvh.me is a community
          return {
            subdomain: potentialSubdomain,
            type: 'community',
            isCustomDomain: false,
          };
        }
      }
      // Handle *.localhost format (existing logic if needed as fallback)
      else if (
        hostname.endsWith('.localhost') ||
        hostname.match(/\.\d+\.\d+\.\d+\.\d+$/)
      ) {
        const parts = hostname.split('.');
        if (parts.length > 1) {
          const subdomain = parts[0];
          if (subdomain === PLATFORM_SUBDOMAIN) {
            return { subdomain, type: 'platform', isCustomDomain: false };
          }
          // For *.localhost, if not platform, assume community for testing
          return { subdomain, type: 'community', isCustomDomain: false };
        }
      }

      // Fallback to URL parameters if no direct subdomain match in dev
      try {
        const params = new URLSearchParams(window.location.search || '');
        const subdomainParam = params.get('subdomain');

        // Get clean subdomain (without path)
        const subdomain = subdomainParam ? subdomainParam.split('/')[0] : null;
        const normalizedSubdomain = normalizeSubdomain(subdomain);

        if (normalizedSubdomain === PLATFORM_SUBDOMAIN) {
          return {
            subdomain: normalizedSubdomain,
            type: 'platform',
            isCustomDomain: false,
          };
        }

        return normalizedSubdomain
          ? {
              subdomain: normalizedSubdomain,
              type: 'community',
              isCustomDomain: false,
            }
          : { subdomain: null, type: 'app', isCustomDomain: false };
      } catch (error) {
        authLogger?.warn(
          { error, context: 'parseDomain' },
          'Error parsing URL parameters'
        );
        return { subdomain: null, type: 'app', isCustomDomain: false };
      }
    }

    // Production environment - handle actual subdomains
    if (typeof window === 'undefined') {
      // Handle server-side rendering
      return { subdomain: null, type: 'app', isCustomDomain: false };
    }

    const hostname = window.location.hostname;
    let parts = hostname.split('.');

    // Special handling for 'www' - treat it as part of the main domain
    if (parts[0] === 'www') {
      parts = parts.slice(1); // Remove 'www' from parts
    }

    // Check if this is a custom domain (not our app domain)
    const endsWithAppDomain = hostname.endsWith(APP_DOMAIN);
    const endsWithAppDomainNoWww = hostname
      .replace('www.', '')
      .endsWith(APP_DOMAIN);
    const isCustomDomain = !endsWithAppDomain && !endsWithAppDomainNoWww;

    // Diagnostic logging for custom domain detection
    console.log('🔍 parseDomain() - Custom domain detection:', {
      hostname,
      APP_DOMAIN,
      endsWithAppDomain,
      endsWithAppDomainNoWww,
      isCustomDomain,
      isDev: isDevelopment(),
    });

    // Handle custom domains
    if (isCustomDomain) {
      authLogger?.info(
        { hostname, isCustomDomain: true },
        'Detected custom domain'
      );
      return {
        subdomain: hostname,
        type: 'community',
        isCustomDomain: true,
      };
    }

    // Handle subdomains (now with www removed from consideration)
    if (parts.length > 2) {
      const subdomain = parts[0] || null; // Ensure subdomain is never undefined
      if (subdomain === PLATFORM_SUBDOMAIN) {
        return { subdomain, type: 'platform', isCustomDomain: false };
      }
      return { subdomain, type: 'community', isCustomDomain: false };
    }

    // Main domain (includes www.domain.com or domain.com)
    return { subdomain: null, type: 'app', isCustomDomain: false };
  } catch (error) {
    // Handle any unexpected errors
    authLogger?.error(
      { error, context: 'parseDomain' },
      'Unexpected error parsing domain'
    );
    return { subdomain: null, type: 'app', isCustomDomain: false };
  }
}

/**
 * Generates a URL with proper subdomain handling for both development and production.
 * Enhanced with better error handling and logging.
 */
export function getSubdomainUrl(
  subdomain: string | null | undefined,
  path: string = ''
): string {
  try {
    const safeSubdomain = normalizeSubdomain(subdomain);
    const safePath = path || '';
    const currentWindow = typeof window !== 'undefined' ? window : null;
    const protocol = currentWindow ? currentWindow.location.protocol : 'https';
    const port =
      currentWindow && currentWindow.location.port
        ? `:${currentWindow.location.port}`
        : '';

    if (
      isDevelopment() &&
      currentWindow &&
      // Check if on lvh.me or a subdomain of it
      (currentWindow.location.hostname === 'lvh.me' ||
        currentWindow.location.hostname.endsWith('.lvh.me'))
    ) {
      // Dev environment using lvh.me (auto-resolves to 127.0.0.1)
      if (safeSubdomain) {
        return `${protocol}//${safeSubdomain}.lvh.me${port}${safePath}`;
      }
      // For the base app URL on lvh.me (e.g., http://lvh.me:3000/onboarding)
      return `${protocol}//lvh.me${port}${safePath}`;
    } else {
      // Production or standard localhost development (using APP_DOMAIN)
      const base = APP_DOMAIN;

      if (safeSubdomain) {
        return `${protocol}//${safeSubdomain}.${base}${safePath}`;
      }

      // For platform pages in production, always use the bare domain (no www)
      // This prevents redirect loops with the useWwwRedirect hook
      if (!isDevelopment() && base.includes('terrarium.app')) {
        // Ensure we use terrarium.app, not www.terrarium.app
        const cleanBase = base.replace('www.', '');
        return `${protocol}//${cleanBase}${safePath}`;
      }

      // For development or other domains, use as-is
      return `${protocol}//${base}${safePath}`;
    }
  } catch (error) {
    authLogger?.error(
      { error, subdomain, path },
      'Failed to generate subdomain URL'
    );
    return '/'; // Fallback to a root path on error
  }
}

/**
 * Redirects to the appropriate subdomain URL.
 * Enhanced with error handling for auth flows.
 */
export function redirectToSubdomain(
  subdomain: string | null | undefined,
  path: string = ''
): void {
  try {
    // Generate the target URL and log the redirection
    const targetUrl = getSubdomainUrl(subdomain, path);
    authLogger?.info(
      { subdomain, path, targetUrl },
      'Redirecting to subdomain'
    );

    // Perform the redirection if window is available
    if (typeof window !== 'undefined') {
      window.location.href = targetUrl;
    }
  } catch (error) {
    // Handle redirection errors
    authLogger?.error(
      { error, subdomain, path },
      'Failed to redirect to subdomain'
    );

    // Attempt to redirect to home as fallback
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  }
}
